"use client";

import { useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Search } from "lucide-react";
import SceneCard from "@/components/places/SceneCard";
import { scenes, usedCategories } from "@/lib/scenes";

/**
 * Grille filtrable du catalogue. 5 scènes en prototype : un simple filtre
 * en mémoire suffit (Fuse.js sera justifié seulement à l'échelle des 115).
 */
export default function CatalogueGrid() {
  // ?q= : point d'entrée de la SearchAction schema.org
  const initialQuery = useSearchParams().get("q") ?? "";
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState<string | null>(null);
  const cats = usedCategories();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scenes.filter((s) => {
      if (category && s.category !== category) return false;
      if (!q) return true;
      return s.title.toLowerCase().includes(q) || (s.commune ?? "").toLowerCase().includes(q);
    });
  }, [query, category]);

  const chipBase = "rounded-full border px-4 py-2 text-xs transition-colors min-h-9 cursor-pointer";

  return (
    <>
      <div className="mt-7 flex flex-wrap items-center gap-2.5">
        <label className="relative">
          <span className="sr-only">Rechercher un lieu</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-mineral"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un lieu…"
            className="w-56 rounded-full border border-white/10 bg-panneau py-2.5 pr-4 pl-10 text-[13px] text-corail placeholder:text-mineral"
          />
        </label>
        <button
          onClick={() => setCategory(null)}
          aria-pressed={category === null}
          className={`${chipBase} ${
            category === null
              ? "border-corail bg-corail font-semibold text-basalte"
              : "border-white/10 text-corail/80 hover:border-or"
          }`}
        >
          Tous
        </button>
        {cats.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(category === cat.id ? null : cat.id)}
            aria-pressed={category === cat.id}
            className={`${chipBase} ${
              category === cat.id
                ? "border-corail bg-corail font-semibold text-basalte"
                : "border-white/10 text-corail/80 hover:border-or"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {filtered.length > 0 ? (
        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((scene) => (
            <SceneCard key={scene.slug} scene={scene} />
          ))}
        </div>
      ) : (
        <p role="status" className="mt-10 text-sm text-mineral">
          Aucun lieu ne correspond à cette recherche.
        </p>
      )}
    </>
  );
}
